import React, { useEffect, useState } from 'react';
import { Box, Container, Heading } from '@chakra-ui/layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/hooks';
import { PageHeader } from 'routes/layouts/pageHeader';
import { ActionBar } from 'components/ActionBar/ActionBar';
import { useUpdateTravelRequestStatus } from 'api/services/TravelRequests';
import {
  Tooltip,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { STATUS_MAPPER, StatusNames } from 'utils/statusUtils';
import { TravelExpensesForm } from 'routes/travelExpenses/TravelExpensesForm';
import { Button } from '@landsvirkjun/react.common';
import { IconDownload } from 'components/Icons';
import {
  ActionParams,
  ActionParamsEnum,
  ShortActionParamsEnum,
  ShortNames,
  StatusObject,
} from 'api/types/types';
import { TravelExpensesPreview } from './TravelExpenses/TravelExpensesPreview';
import { RejectionModal } from './TravelRequest/RejectionModal';
import { TravelRequestPreview } from './TravelRequest/TravelRequestPreview';
import { DailyAllowanceForm } from '../travelExpenses/DailyAllowance/DailyAllowanceForm';
import { useRetrieveTravelRequestById } from 'api/services/TravelForm';
import {
  useGetAttachmentForTravelRequest,
  useRetrieveDailyAllowancesById,
  useRetrieveExpensesById,
} from 'api/services/TravelExpenses';
import { ActivityLog } from 'routes/activityLog/ActivityLog';
import { FinishCostModal } from './FinishCostModal';
import {
  getPrimaryBtnText,
  getSecondaryBtnText,
  getSecondaryBtnVariant,
} from './actionUtils';
import { downloadFileFromBase64 } from 'utils/base64ToBlob';
import { useAuth } from 'components/Auth/AuthProvider';
import { TravelExpensesInfo } from './TravelExpenses/TravelExpenseInfo';
import { TravelVoucherform } from 'routes/travelVouchers/form/TravelVoucherForm';
import { toast } from 'react-toastify';

export const tabStyles = {
  style: {
    color: '#939598',
    fontWeight: 600,
    borderBottom: '3px solid transparent',
  },
  _selected: {
    color: '#000 !important',
    borderBottom: '3px solid #3C90E4 !important',
  },
  _active: {
    backgroundColor: 'transparent',
  },
};

export const PreviewTabs = ({}): JSX.Element => {
  const location = useLocation();
  const { hasAccess } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const { id = null, action = '' } = Object.fromEntries(queryParams);
  const { hasRoles, userRoles, user } = useAuth();
  const { hasActions, role, erpId, edit } = location.state || {};
  const {
    data: travelRequestData,
    isLoading: isLoadingRequestById,
    isError: isErrorRequestById,
  } = useRetrieveTravelRequestById(id, role);

  const { refetch: getAttachmentForTravelRequest } =
    useGetAttachmentForTravelRequest(id);
  const adminPath = location?.pathname?.startsWith('/admin/') ? '/admin' : '';

  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();

  const [editRequest, setEditRequest] = useState<boolean>(
    (edit as boolean) || false
  );
  const [travelStatus, setTravelStatus] = useState<StatusObject>();
  const [reasonForDenied, setReasonForDenied] = useState('');
  const [manualFetchTrigger, setManualFetchTrigger] = useState(false);
  const [selectedTab, setSelectedTab] = useState(action || 'beidni');
  const [pdfFile, setPdfFile] = useState({
    fileContents: '',
    fileName: '',
  });

  const isPending = travelStatus?.shortName === 'PENDING';
  const isAuthorized = travelStatus?.shortName === 'AUTHORIZED';
  const isFinished = travelStatus?.shortName === 'COST_IN_REVIEW';
  const isRevisit = travelStatus?.shortName === 'COST_REVISIT';
  const isReviewed = travelStatus?.shortName === 'COST_REVIEWED';
  const isConfirmed = travelStatus?.shortName === 'COST_CONFIRMED';
  const isApproved = travelStatus?.shortName === 'COST_APPROVED';
  const isCompleted = travelStatus?.shortName === 'COMPLETED';
  const isDenied = travelStatus?.shortName === 'DENIED';

  const allowPDFDownload =
    isReviewed || isConfirmed || isApproved || isCompleted;
  const allowEditRequest =
    (isRevisit && action === 'beidni') || (isAuthorized && action === 'beidni');
  const {
    data: expenseData = [],
    refetch: fetchExpenses,
    isFetching: isFetchingExpenses,
    isError: expensesError,
  } = useRetrieveExpensesById(id, manualFetchTrigger);

  const {
    data: dailyAllowanceExpenseData = [],
    refetch: fetchDailyAllowanceExpense,
    isFetching: isFetchingDailyAllowanceExpenses,
    isError: dailyAllowanceExpenseError,
  } = useRetrieveDailyAllowancesById(id, manualFetchTrigger);

  useEffect(() => {
    if (travelRequestData) {
      setTravelStatus(travelRequestData.status);
    }
  }, [travelRequestData, setTravelStatus]);

  useEffect(() => {
    if (id && travelStatus && !isDenied && !isPending) {
      setManualFetchTrigger(true);
    }
  }, [id, travelStatus, isPending, isDenied]);

  useEffect(() => {
    if (edit === false) {
      setEditRequest(false);
    }
  }, [edit]);

  const isEdit = (): boolean | null => {
    const hasExtendedRights: boolean = (userRoles &&
      userRoles.length > 0) as boolean;
    const hasEditRights: boolean =
      hasExtendedRights && (hasRoles('travel-request-edit') as boolean);
    const hasViewRights: boolean =
      hasExtendedRights && (hasRoles('travel-request-view') as boolean);
    const isOwner =
      user?.adUser === travelRequestData?.employeeId ||
      user?.adUser === travelRequestData?.createdBy;

    if (isAuthorized || isRevisit) {
      if (isOwner) return true;
      if (hasExtendedRights) {
        return hasEditRights;
      }
      return true;
    }

    if (isFinished || isReviewed || isConfirmed || isApproved || isCompleted) {
      if (hasExtendedRights) {
        return !hasViewRights;
      }
      return false;
    }

    return null;
  };

  const showDailyAllowanceTab = isEdit();

  const handleTabsChange = (index: number): void => {
    let actionString: ActionParams | string = 'beidni';
    switch (index) {
      case 0:
        actionString = ActionParamsEnum[ActionParamsEnum.beidni];
        break;
      case 1:
        actionString = ActionParamsEnum[ActionParamsEnum.kostnadur];
        break;
      case 2:
        if (showDailyAllowanceTab) {
          actionString = ActionParamsEnum[ActionParamsEnum.dagpeningar];
        } else {
          actionString = ActionParamsEnum[ActionParamsEnum.adgerdarsaga];
        }
        break;
      case 3:
        actionString = ActionParamsEnum[ActionParamsEnum.adgerdarsaga];
        break;
      default:
        break;
    }

    navigate(
      `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=${actionString}`,
      {
        state: { hasActions: hasActions, role: role },
      }
    );
    setSelectedTab(actionString);
  };
  const {
    mutate: updateStatus,
    isLoading: isUpdatingStatus,
    isSuccess: updatedTravelRequestStatus,
  } = useUpdateTravelRequestStatus(id);

  useEffect(() => {
    if (updatedTravelRequestStatus) {
      if (role && role === 'approver') {
        navigate('/samthykktir');
      } else {
        navigate('/ferdabeidnir');
      }
    }
    // eslint-disable-next-line
  }, [updatedTravelRequestStatus]);

  const onApproveRequest = (): void => {
    let shortName: ShortNames;

    if (isAuthorized || isRevisit) {
      shortName = 'COST_IN_REVIEW';
    } else if (isFinished) {
      shortName = 'COST_REVISIT';
    } else if (isReviewed) {
      shortName = 'COST_CONFIRMED';
    } else if (isConfirmed) {
      shortName = 'COST_APPROVED';
    } else if (isApproved) {
      shortName = 'COST_REVISIT';
    } else if (isPending) {
      shortName = 'AUTHORIZED';
    } else {
      shortName = 'PENDING';
    }
    const errorText = travelStatus?.shortName
      ? `Ekki gekk að framkvæma aðgerð á beiðni í stöðunni "${
          STATUS_MAPPER[travelStatus?.shortName].label
        }"  reyndu aftur síðar`
      : 'Ekki tókst að framkvæma aðgerð, reyndu aftur síðar';

    if (shortName === 'PENDING') {
      toast.warning(errorText);
      return;
    }
    const data: StatusObject = {
      shortName,
    };
    updateStatus(data);
  };

  const onRejectRequest = (): void => {
    const data: StatusObject = {
      shortName:
        isReviewed || isConfirmed || isFinished || isApproved
          ? 'COST_REVISIT'
          : 'DENIED',
      comment: reasonForDenied || '',
    };
    updateStatus(data);
  };

  const generatePDFReport = (): void => {
    getAttachmentForTravelRequest().then((res) => {
      const data = res.data;
      const { data: attachmentData } = data as any;

      if (attachmentData && attachmentData.length > 0) {
        const { fileContents, fileType, expNumber } = attachmentData[0];
        const fileName = `Kostnaðarskýrsla_${id}_${expNumber}.${fileType}`;

        setPdfFile({ fileContents, fileName });
      }
    });
  };

  useEffect(() => {
    if (allowPDFDownload) {
      setPdfFile({ fileContents: '', fileName: '' });
      generatePDFReport();
    }
    // eslint-disable-next-line
  }, [allowPDFDownload]);

  const showActionBar = (): boolean => {
    const isApproverForRequest =
      user?.adUser?.toUpperCase() ===
      travelRequestData?.approverId?.toUpperCase();

    if (isPending || isConfirmed) {
      if (isApproverForRequest && hasAccess()) {
        return true;
      } else {
        return false;
      }
    } else if (isDenied || isCompleted) {
      return false;
    } else if (travelRequestData?.message) return false;
    return true;
  };
  const loadingData =
    isLoadingRequestById || isFetchingExpenses || dailyAllowanceExpenseError;

  return (
    <Container maxW="container.xl" marginStart="ltr" pl={0}>
      <PageHeader
        left={
          <Box alignItems="center" display="flex">
            <Heading as="h2" size="lg" mr={2}>
              {travelRequestData?.employeeName}
            </Heading>
          </Box>
        }
        right={
          travelStatus && (
            <Box>
              <Tooltip label={travelStatus.description}>
                <Badge
                  colorScheme={
                    STATUS_MAPPER[travelStatus.shortName as keyof StatusNames]
                      ?.color
                  }
                  cursor="pointer"
                >
                  {
                    STATUS_MAPPER[travelStatus.shortName as keyof StatusNames]
                      ?.label
                  }
                </Badge>
              </Tooltip>
              {allowPDFDownload && pdfFile.fileContents && (
                <Box>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<IconDownload />}
                    mt={5}
                    onClick={(): void =>
                      downloadFileFromBase64(
                        pdfFile.fileContents,
                        pdfFile.fileName
                      )
                    }
                  >
                    Sækja skýrslu
                  </Button>
                </Box>
              )}
              {/* {isCompleted && !pdfFile.fileContents && (
                <Box>Skýrsla ekki tiltæk</Box>
              )} */}
            </Box>
          )
        }
      />
      {!isErrorRequestById && (
        <Tabs
          size="md"
          mt={10}
          index={
            showDailyAllowanceTab
              ? ActionParamsEnum[selectedTab]
              : ShortActionParamsEnum[selectedTab]
          }
          onChange={handleTabsChange}
        >
          <TabList>
            <Tab {...tabStyles}>Ferðabeiðni</Tab>
            <Tab isDisabled={isDenied || editRequest} {...tabStyles}>
              Kostnaður
            </Tab>
            {showDailyAllowanceTab && (
              <Tab isDisabled={editRequest} {...tabStyles}>
                Dagpeningar
              </Tab>
            )}
            <Tab isDisabled={editRequest} {...tabStyles}>
              Aðgerðarsaga
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel padding={0}>
              <Container maxW="none" mt={12} marginStart="ltr" p={0}>
                {!editRequest ? (
                  <TravelRequestPreview
                    travelRequestData={travelRequestData}
                    isLoadingRequestById={isLoadingRequestById}
                  />
                ) : (
                  <TravelVoucherform
                    editMode={editRequest}
                    onCancel={(): void => setEditRequest(false)}
                  />
                )}
              </Container>
            </TabPanel>
            <TabPanel padding={0}>
              <Container maxW="none" mt={12} marginStart="ltr" p={0}>
                {isEdit() ? (
                  <TravelExpensesForm
                    fetchExpenses={fetchExpenses}
                    travelRequestData={travelRequestData}
                    expenseData={expenseData}
                    expensesError={expensesError}
                    isFetchingExpenses={isFetchingExpenses}
                  />
                ) : isEdit() === false ? (
                  <TravelExpensesPreview
                    expenseData={dailyAllowanceExpenseData.concat(expenseData)}
                    expensesError={expensesError}
                    isFetchingExpenses={isFetchingExpenses}
                  />
                ) : (
                  <TravelExpensesInfo />
                )}
              </Container>
            </TabPanel>
            <TabPanel padding={0}>
              <Container maxW="none" mt={10} marginStart="ltr" p={0}>
                {isEdit() ? (
                  <DailyAllowanceForm
                    travelRequestData={travelRequestData}
                    dailyAllowanceExpenseData={dailyAllowanceExpenseData}
                    dailyAllowanceExpenseError={dailyAllowanceExpenseError}
                    isFetchingDailyAllowanceExpenses={
                      isFetchingDailyAllowanceExpenses
                    }
                    fetchDailyAllowanceExpense={fetchDailyAllowanceExpense}
                  />
                ) : (
                  <ActivityLog showHeader={false} />
                )}
              </Container>
            </TabPanel>
            <TabPanel padding={0}>
              <Container maxW="container.xl" mt={10} marginStart="ltr" p={0}>
                <ActivityLog showHeader={false} />
              </Container>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
      {!editRequest && (
        <Box as="div" mt={20}>
          <ActionBar
            showActionButtons={showActionBar() && !editRequest}
            editMode={editRequest}
            allowEditRequest={allowEditRequest}
            onEditRequest={(): void => setEditRequest(!editRequest)}
            primaryBtnText={getPrimaryBtnText(travelStatus?.shortName, action)}
            secondaryBtnText={getSecondaryBtnText(travelStatus?.shortName)}
            secondaryBtnVariant={getSecondaryBtnVariant(
              travelStatus?.shortName
            )}
            secondaryBtnOnClick={(): void =>
              !isPending && !isReviewed && !isConfirmed
                ? navigate('/ferdabeidnir')
                : onOpen()
            }
            primaryBtnOnClick={(): void | (() => void) =>
              isAuthorized && action === 'kostnadur'
                ? handleTabsChange(2)
                : isRevisit || (isAuthorized && action === 'dagpeningar')
                ? onConfirmOpen()
                : isFinished || isApproved
                ? onOpen()
                : onApproveRequest()
            }
            disablePrimaryBtn={
              (isAuthorized || isRevisit || loadingData) &&
              expenseData.length <= 0 &&
              !editRequest &&
              action === 'beidni'
            }
            onOpen={onOpen}
            isLoading={isUpdatingStatus}
          />
        </Box>
      )}
      <RejectionModal
        isOpen={isOpen}
        onClose={onClose}
        reasonForDenied={reasonForDenied}
        setReasonForDenied={setReasonForDenied}
        onRejectRequest={onRejectRequest}
        status={travelStatus?.shortName || 'default'}
        isUpdatingStatus={isUpdatingStatus}
        isReviewed={isReviewed}
      />
      <FinishCostModal
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        onApproveRequest={onApproveRequest}
        isUpdatingStatus={isUpdatingStatus}
      />
    </Container>
  );
};
